import { supabase } from '@/lib/supabase';

const ORG_ID = '00000000-0000-0000-0000-000000000001';

export interface Utilisateur {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  statut: string;
  modules: string[];
  roles: string[];
}

interface UtilisateurRow {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  statut: string;
  preferences: { modules?: string[] } | null;
  utilisateur_roles: { roles: { nom: string } | null }[] | null;
}

export async function listUtilisateurs(): Promise<Utilisateur[]> {
  const { data, error } = await supabase
    .from('utilisateurs')
    .select('id,email,nom,prenom,statut,preferences,utilisateur_roles(roles(nom))')
    .is('deleted_at', null)
    .order('nom');
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as UtilisateurRow[]).map((u) => ({
    id: u.id,
    email: u.email,
    nom: u.nom,
    prenom: u.prenom,
    statut: u.statut,
    modules: u.preferences?.modules ?? [],
    roles: (u.utilisateur_roles ?? []).map((ur) => ur.roles?.nom).filter((n): n is string => Boolean(n)),
  }));
}

export async function updateUtilisateurModules(id: string, modules: string[]): Promise<void> {
  const { data } = await supabase.from('utilisateurs').select('preferences').eq('id', id).single();
  const prefs = ((data?.preferences as Record<string, unknown> | null) ?? {}) as Record<string, unknown>;
  const { error } = await supabase
    .from('utilisateurs')
    .update({ preferences: { ...prefs, modules } })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// Enregistre un email AUTORISÉ (sans compte ni email envoyé). La personne créera
// elle-même son compte via « Première connexion » sur l'écran de login.
export async function ajouterUtilisateur(input: {
  email: string;
  nom: string;
  prenom: string;
  modules: string[];
}): Promise<void> {
  const email = input.email.trim().toLowerCase();
  if (email === '') throw new Error('Email obligatoire.');
  const { data: existing } = await supabase
    .from('utilisateurs')
    .select('id')
    .eq('email', email)
    .is('deleted_at', null)
    .maybeSingle();
  if (existing) throw new Error('Cet email est déjà enregistré.');
  const { error } = await supabase.from('utilisateurs').insert({
    organization_id: ORG_ID,
    email,
    nom: input.nom.trim(),
    prenom: input.prenom.trim(),
    statut: 'invite',
    preferences: { modules: input.modules },
  });
  if (error) throw new Error(error.message);
}

// Première connexion : la personne (dont l'email est autorisé) crée son compte +
// mot de passe. Côté serveur (fonction Edge), AUCUN email n'est envoyé.
export async function premiereConnexion(email: string, password: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('premiere-connexion', {
    body: { email: email.trim().toLowerCase(), password },
  });
  if (error) {
    let msg = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = (await ctx.json()) as { error?: string };
        if (body?.error) msg = body.error;
      } catch {
        /* garde le message par défaut */
      }
    }
    throw new Error(msg);
  }
  const res = data as { ok?: boolean; error?: string } | null;
  if (res?.error) throw new Error(res.error);
  if (!res?.ok) throw new Error('Échec de la création du compte.');
}
